import React from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LaunchIcon from '@mui/icons-material/Launch';
import ReputationPill from '../common/ReputationPill';
import { IdeaData } from '../../hooks/useIdeaResults';

interface RelatedSimulationsSectionProps {
  ideaData: IdeaData;
  draftId: string;
  expandedSimulations: { [key: string]: boolean };
  onToggleSimulations: (draftId: string) => void;
  getInitialSimulationLimit: () => number;
}

const RelatedSimulationsSection: React.FC<RelatedSimulationsSectionProps> = ({
  ideaData,
  draftId,
  expandedSimulations,
  onToggleSimulations,
  getInitialSimulationLimit
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!ideaData.relatedSimulationsByDraft || 
      !ideaData.relatedSimulationsByDraft[draftId] || 
      ideaData.relatedSimulationsByDraft[draftId].length === 0) {
    return null;
  }

  const simulations = ideaData.relatedSimulationsByDraft[draftId].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const initialLimit = getInitialSimulationLimit();
  const isExpanded = expandedSimulations[draftId] || false;
  const visibleSimulations = isExpanded ? simulations : simulations.slice(0, initialLimit);
  const remainingCount = Math.max(0, simulations.length - initialLimit);

  return (
    <Box sx={{ 
      mb: 3,
      p: 3,
      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F8F9FA',
      borderRadius: 1,
      border: 1,
      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF'
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        Related Simulations ({simulations.length})
      </Typography>
      
      {/* Mobile: Card-based layout - NO horizontal scrolling */}
      {isMobile ? (
        <Stack spacing={1}>
          {visibleSimulations.map((sim) => {
            const forecast = (sim.resultJson?.data?.forecast) || {};
            const uiScore = Math.round(
              forecast?.reputation?.uiScore ?? forecast?.score ?? 0
            );
            const level: string = forecast?.reputation?.level || 'Pending';
            const color: string | undefined = forecast?.reputation?.color;
            
            // Ultra-compact date format for mobile
            const formattedDate = new Date(sim.createdAt).toLocaleDateString('en-US', {
              month: 'numeric',
              day: 'numeric'
            });

            return (
              <Paper
                key={sim.id}
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#FAFAFA',
                  border: 1,
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.12)' : '#F0F8FF',
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.4)' : '#118AB2',
                    transform: 'translateY(-1px)',
                    boxShadow: 1
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.16)' : '#E5F4FC'
                  }
                }}
                onClick={() => window.open(`/simulations/${sim.id}/results`, '_blank', 'noopener,noreferrer')}
              >
                {/* Top row: Date and Platform */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 1.5 
                }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    color: (theme) => theme.palette.mode === 'dark' ? '#cbd5e1' : '#495057',
                    fontSize: '0.875rem'
                  }}>
                    {formattedDate}
                  </Typography>
                  <Chip 
                    label={sim.platform || 'Unknown'} 
                    size="small" 
                    sx={{ 
                      height: '24px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.2)' : '#118AB2',
                      color: (theme) => theme.palette.mode === 'dark' ? '#9bd1e3' : 'white'
                    }} 
                  />
                </Box>

                {/* Bottom row: Reputation score inline with level */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center' 
                }}>
                  {/* Reputation info - ultra compact */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1,
                        py: 0.25,
                        borderRadius: '12px',
                        bgcolor: (theme) => color 
                          ? (theme.palette.mode === 'dark' ? `${color}30` : `${color}20`)
                          : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f5f5f5'),
                        border: 1,
                        borderColor: (theme) => color || (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#ddd')
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          color: (theme) => color || (theme.palette.mode === 'dark' ? '#cbd5e1' : '#666'),
                          lineHeight: 1
                        }}
                      >
                        {uiScore}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ 
                      color: color || ((theme) => theme.palette.mode === 'dark' ? '#94a3b8' : '#666'),
                      fontWeight: 500,
                      fontSize: '0.75rem'
                    }}>
                      {level.split(' ')[0]} {/* Just first word like "Strong", "Mild" */}
                    </Typography>
                  </Box>

                  {/* Privacy indicator - minimal */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ 
                      fontSize: '0.75rem', 
                      fontWeight: 500,
                      color: (theme) => theme.palette.mode === 'dark' ? '#94a3b8' : 'inherit'
                    }}>
                      {sim.isPrivate ? 'Private' : 'Public'}
                    </Typography>
                    <LaunchIcon 
                      sx={{ 
                        fontSize: '1rem', 
                        color: (theme) => theme.palette.mode === 'dark' ? '#9bd1e3' : '#118AB2',
                        opacity: 0.7
                      }} 
                    />
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        /* Desktop: Table layout - keeping simple for now */
        <Stack spacing={1}>
          {visibleSimulations.map((sim) => {
            const forecast = (sim.resultJson?.data?.forecast) || {};
            const uiScore = Math.round(
              forecast?.reputation?.uiScore ?? forecast?.score ?? 0
            );
            const level: string = forecast?.reputation?.level || 'Pending';
            const color: string | undefined = forecast?.reputation?.color;
            
            const formattedDate = new Date(sim.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            });

            return (
              <Box
                key={sim.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#FAFAFA',
                  border: 1,
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.12)' : '#F0F8FF',
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.4)' : '#118AB2'
                  }
                }}
                onClick={() => window.open(`/simulations/${sim.id}/results`, '_blank', 'noopener,noreferrer')}
              >
                <Stack direction="row" spacing={3} alignItems="center" sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, minWidth: '120px' }}>
                    {formattedDate}
                  </Typography>
                  <Chip 
                    label={sim.platform || 'Unknown'} 
                    size="small" 
                    sx={{ 
                      height: '20px',
                      fontSize: '0.7rem',
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF',
                      color: (theme) => theme.palette.mode === 'dark' ? '#cbd5e1' : 'inherit',
                      '& .MuiChip-label': { px: 1 }
                    }} 
                  />
                  <ReputationPill 
                    uiScore={uiScore} 
                    level={level} 
                    color={color}
                  />
                  <Chip 
                    label={sim.isPrivate ? 'Private' : 'Public'}
                    size="small"
                    sx={{ 
                      height: '20px',
                      fontSize: '0.7rem',
                      bgcolor: (theme) => sim.isPrivate 
                        ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F8F9FA')
                        : (theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.12)' : '#E5F4FC'),
                      color: (theme) => sim.isPrivate 
                        ? (theme.palette.mode === 'dark' ? '#94a3b8' : '#6B7A8D')
                        : (theme.palette.mode === 'dark' ? '#9bd1e3' : '#118AB2'),
                      border: 1,
                      borderColor: (theme) => sim.isPrivate 
                        ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#DEE2E6')
                        : (theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.35)' : '#BBDEFB'),
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                </Stack>
                <LaunchIcon sx={{ fontSize: '1rem', color: (theme) => theme.palette.mode === 'dark' ? '#9bd1e3' : '#118AB2', opacity: 0.7 }} />
              </Box>
            );
          })}
        </Stack>
      )}
      
      {/* Expansion Controls */}
      {remainingCount > 0 && !isExpanded && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSimulations(draftId);
            }}
            startIcon={<ExpandMoreIcon />}
            sx={{ 
              textTransform: 'none',
              fontSize: '0.75rem',
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#E9ECEF',
              color: (theme) => theme.palette.mode === 'dark' ? '#cbd5e1' : '#495057',
              '&:hover': { 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F8F9FA',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : '#DEE2E6'
              }
            }}
          >
            Show {remainingCount} more simulation{remainingCount !== 1 ? 's' : ''}
          </Button>
        </Box>
      )}
      
      {isExpanded && simulations.length > initialLimit && (
        <Box sx={{ textAlign: 'left', mt: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSimulations(draftId);
            }}
            startIcon={<ExpandLessIcon />}
            sx={{ 
              textTransform: 'none',
              color: (theme) => theme.palette.mode === 'dark' ? '#94a3b8' : '#6B7A8D',
              fontSize: '0.75rem',
              '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F8F9FA' }
            }}
          >
            Show less
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default RelatedSimulationsSection;